import * as React from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  X,
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command";
import { cn } from "../../utils/cn";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Button } from "../button";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../tooltip";

interface Option {
  labelAr: string;
  labelEn: string;
  value: number | string;
}

interface ComboboxInputProps {
  options?: Option[];
  onChange: (value: number | string) => void;
  value?: string;
  error?: string;
  label?: string;
  required?: boolean;
  searchValue?: string;
  setSearchValue?: React.Dispatch<React.SetStateAction<string>>;
  isPagination?: boolean;
  page?: number;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  totalPages?: number;
  errorStyle?: string;
  labelStyle?: string;
  readOnly?: boolean;
  isInfoToolTip?: boolean;
  infoToolTip?: React.ReactNode;
  isSearch?: boolean;
  isClear?: boolean;
  styles?: string;
  isIncreasePopoverContentZIndex?: boolean;
  placeholder?: string;
  valueStyles?: string;
  chevronsStyles?: string;
}

function ComboboxInput({
  options = [],
  onChange,
  value,
  error,
  label,
  required,
  searchValue,
  setSearchValue,
  isPagination = false,
  page,
  setPage,
  totalPages,
  labelStyle,
  errorStyle,
  readOnly = false,
  isInfoToolTip = false,
  infoToolTip,
  isSearch = true,
  isClear = true,
  styles,
  isIncreasePopoverContentZIndex = false,
  placeholder,
  valueStyles,
  chevronsStyles,
}: ComboboxInputProps) {
  const [open, setOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState<string>(value || "");
  const [storedValue, setStoredValue] = React.useState<string>(selectedValue);

  const { t, i18n } = useTranslation();

  React.useEffect(() => {
    if (value) {
      setSelectedValue(value);
    } else {
      setSelectedValue("");
    }
  }, [value]);

  const displayedValue = React.useMemo(() => {
    if (selectedValue) {
      setStoredValue(selectedValue);
      const selectedOption = options.find((option) => {
        if (
          typeof option.labelEn === "string" ||
          typeof option.labelAr === "string"
        ) {
          return (
            option.labelEn === selectedValue || option.labelAr === selectedValue
          );
        } else {
          return option.value === selectedValue;
        }
      });

      return i18n.language === "en"
        ? selectedOption?.labelEn
        : selectedOption?.labelAr;
    } else {
      return !placeholder ? `${t("select")}...` : placeholder;
    }
  }, [i18n.language, options, selectedValue, t, placeholder]);

  const handleClearSearch = React.useCallback(() => {
    onChange("");
    setStoredValue("");
    setSelectedValue("");
  }, [onChange]);

  return (
    <div className="relative  flex flex-col gap-[3px]">
      <div className="flex items-center gap-2">
        <label className={cn("text-[13px] font-normal", labelStyle)}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        {isInfoToolTip && <CustomTooltip>{infoToolTip}</CustomTooltip>}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={readOnly}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between border-2 rounded-[15px] py-3 px-4 !text-gray-primary",
              open ? "border-purple-secondary" : "border-light",
              styles,
            )}
          >
            <span
              className={cn(
                "truncate w-[130px] flex-1",
                i18n.language === "en" ? "text-left" : "text-right",
                valueStyles,
              )}
            >
              {selectedValue && !displayedValue ? storedValue : displayedValue}
            </span>
            <div className="flex items-center">
              {isClear && (
                <X
                  onClick={handleClearSearch}
                  className="h-4 w-4 shrink-0 opacity-50"
                />
              )}
              <ChevronsUpDown
                className={cn("h-4 w-4 shrink-0 opacity-50", chevronsStyles)}
              />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "w-[--radix-popover-trigger-width] p-0 bg-white",
            isIncreasePopoverContentZIndex && "z-[99999999]",
          )}
        >
          <Command className="w-full">
            {isSearch && (
              <CommandInput
                placeholder={`${t("select")}...`}
                className="w-full dark:text-black "
                value={searchValue}
                onValueChange={setSearchValue}
              />
            )}
            <CommandList className="w-full">
              <CommandEmpty>{t("noOptionFound")}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={
                      i18n.language === "en" ? option.labelEn : option.labelAr
                    }
                    onSelect={() => {
                      onChange(option.value);
                      setSelectedValue(
                        i18n.language === "en"
                          ? option.labelEn
                          : option.labelAr,
                      );
                      setOpen(false);
                    }}
                    className="w-full hover:bg-purple-primary/10 dark:text-black cursor-pointer rounded-md"
                  >
                    <div className="mr-0.5 h-4 w-4">
                      <Check
                        className={cn(
                          "h-4 w-4",
                          selectedValue === option.labelEn ||
                            selectedValue === option.labelEn
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                    </div>
                    {i18n.language === "en" ? option.labelEn : option.labelAr}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>

            {isPagination && (
              <div className="flex justify-end items-center gap-1 py-2 pr-3">
                <Button
                  className="p-1 disabled:bg-gray-900"
                  onClick={() => setPage?.((prev) => prev - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft size={12} />
                </Button>
                <Button
                  className="p-1 disabled:bg-gray-900"
                  onClick={() => setPage?.((prev) => prev + 1)}
                  disabled={page === totalPages}
                >
                  <ChevronRight size={12} />
                </Button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      {!!error && (
        <p
          className={cn(
            "text-red-600 text-[10px] mt-[1px] absolute -bottom-[15px] italic",
            errorStyle,
            i18n.language === "en" ? "left-[2px]" : "right-[2px]",
          )}
        >
          {error}
        </p>
      )}
    </div>
  );
}

export default React.memo(ComboboxInput);
